import { html, TemplateResult } from "lit-html";
import { getUser, getEmailProviders } from '@services/email-service';
import { isValidEmail, focusHandler } from "@utils/index";
import { EmailTypeaheadTemplate } from "../templates";
import {
  removeEmailProvidersList,
  renderEmailProvidersList,
  renderEmailTypeaheadTemplate,
  HotKeys
} from "../config";

const setState = (initialState: IEmailTypeaheadComponent, newState: IEmailTypeaheadComponent): IEmailTypeaheadComponent =>
  Object.assign(initialState, newState);

const EmailTypeaheadComponent = (props: IEmailTypeaheadProps): TemplateResult => {

  const state: IEmailTypeaheadComponent = {
    providersList: [],
    suggestedEmailValue: "",
    activeProviderIndex: 0,
    email: {
      value: "",
      isValid: false
    },
    handleClick,
    handleInputChange,
    handleMouseEnter,
    handleKeyDown,
  }

  function handleInputChange(event: Event): void {
    const { value: emailAddress = "" } = <HTMLInputElement>event.target;
    props.onEmailChange?.(emailAddress);

    const providersList: IAutocompleteEmailProvider[] = getEmailProviders(emailAddress);
    const isValid: boolean = isValidEmail(emailAddress);

    if (providersList.length) {
      const suggestedEmailValue: string = getUser(emailAddress) + providersList[0].label;
      setState(state, { ...state, email: { value: emailAddress, isValid }, providersList, activeProviderIndex: 0, suggestedEmailValue });

      renderEmailProvidersList(state, props);
    } else {
      setState(state, { ...state, email: { value: emailAddress, isValid }, providersList, activeProviderIndex: 0, suggestedEmailValue: "" })

      removeEmailProvidersList();
    }

    renderEmailTypeaheadTemplate(state, props);
    focusHandler(".ts-email-typeahead");
  }

  function handleClick(event: MouseEvent): void {
    const element: HTMLElement = (<HTMLElement>event.target);
    const label: string = element.innerText;

    const emailAddress = getUser(state.email.value) + label

    setState(state, { ...state, email: { value: emailAddress, isValid: isValidEmail(emailAddress) }, providersList: [], suggestedEmailValue: "" });
    renderEmailTypeaheadTemplate(state, props);

    removeEmailProvidersList();

    props.onEmailChange?.(emailAddress);
  }

  function handleMouseEnter(index: number): void {
    setState(state, { ...state, activeProviderIndex: index })

    renderEmailProvidersList(state, props)
  }

  function handleKeyDown(event: KeyboardEvent): void {
    const { key } = event;

    if (Object.values(HotKeys).includes(key as HotKeys) &&
      state.providersList.length &&
      !state.email.isValid) {

      event.preventDefault();
      event.stopPropagation();

      if (key === HotKeys.ARROW_DOWN && state.activeProviderIndex < state.providersList.length - 1) {
        const activeProviderIndex = state.activeProviderIndex + 1;

        setState(state, { ...state, activeProviderIndex });

        renderEmailProvidersList(state, props);

        return;
      }

      if (key === HotKeys.ARROW_UP && state.activeProviderIndex > 0) {
        const activeProviderIndex = state.activeProviderIndex - 1;

        setState(state, { ...state, activeProviderIndex });

        renderEmailProvidersList(state, props);

        return;
      }

      if (key === HotKeys.ENTER || key === HotKeys.TAB) {
        const { activeProviderIndex } = state;
        const { label = "" } = state.providersList[activeProviderIndex];
        const value = getUser(state.email.value) + label;

        setState(state, { ...state, email: { value, isValid: isValidEmail(value) }, activeProviderIndex, suggestedEmailValue: "" });

        renderEmailTypeaheadTemplate(state, props);
        removeEmailProvidersList();

        props.onEmailChange?.(value);

        return;
      }

      if (key === HotKeys.ESCAPE) {
        setState(state, { ...state, providersList: [], activeProviderIndex: 0 });

        removeEmailProvidersList();
      }
    }
  }

  function template(): TemplateResult {
    return html`
      <div class="email-typeahead-wrapper">
        ${EmailTypeaheadTemplate(state, props)}
      </div>
      <div class="providers-wrapper" aria-hidden="false"></div>
    `;
  }

  return template();
}

export default EmailTypeaheadComponent;